/**
 * Native push notification registration and handling.
 *
 * ============================================================================
 *  WHAT THIS FILE OWNS, AND WHAT IT DOES NOT
 * ============================================================================
 *
 * Owns (Phase 38): obtaining a platform token, telling the API about it,
 * removing it on logout, and routing a notification tap to a screen.
 *
 * Does NOT own (Phase 39): *when* the member is asked for permission, and the
 * per-category toggles. Phase 38 deliberately exposes `requestPermission()` as
 * something a caller invokes at a moment of its choosing, and never calls it on
 * app launch. Asking at launch is the single fastest way to get permanently
 * denied -- iOS gives you exactly one prompt, and a member who declines it
 * cannot be asked again from inside the app.
 *
 * The platform token is not a secret, but it is a device identifier tied to a
 * member, so it is never logged.
 */

import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';

/**
 * Notification categories. These MUST match the cases of the API's
 * NotificationCategory enum -- on Android each one becomes a notification
 * channel, and a channel's importance is immutable once created on a device.
 * Renaming one after release means shipping a new channel id, not editing this.
 *
 * The parity assertion lives in tests/js/push-taxonomy.test.js.
 *
 * Phase 38 carried this same "MUST match" comment while claiming a test enforced
 * it, and no such test existed: the one that did
 * (tests/store/push.test.js, `matches the API PushNotification constants`)
 * compared this object to a hardcoded FOUR-key literal, so it asserted the drift
 * instead of detecting it. `marketing` was missing from this list for a whole
 * phase and every gate stayed green. The replacement asserts the five category
 * VALUES against a locked list, which fails the moment this object and the
 * server's enum stop agreeing on either side.
 *
 * Only the values are the contract. The KEYS here are a JS convenience; the
 * server has no notion of them.
 */
export const CATEGORIES = {
  BOOKING: 'booking',
  PAYMENT: 'payment',
  OPERATIONAL: 'operational',
  NEWS: 'news',
  MARKETING: 'marketing',
};

/**
 * Maps a notification's `screen` value to a route in this app.
 *
 * An allowlist on purpose. The payload never carries a URL, and this map is the
 * reason a malformed or hostile payload cannot navigate the WebView somewhere
 * arbitrary -- an unknown screen falls back to home rather than being followed.
 *
 * Every value here is cross-checked against the REAL route table in
 * src/js/routes.js by tests/js/push-taxonomy.test.js. That cross-check exists
 * because the previous test compared this map with itself
 * (`expect(resolveScreenRoute('reserveringen')).toBe('/reserveringen/')`), which
 * is a constant equalling a constant and would have passed with every route in
 * the app broken -- as indeed it did: `reserveringen` pointed at
 * `/reserveringen/` and `abonnement` at `/abonnement/`, neither of which has
 * ever existed in routes.js, so a tapped notification landed on the `(.*)`
 * catch-all 404.
 *
 * WHICH KEY IS FOR WHAT, because two of these look interchangeable and are not:
 *
 *   `abonnement` -> /account/payments, which is a SHOP, and which is therefore
 *     NOT the page called Abonnement. Re-measured at plan 40-05: Drinken
 *     EUR 2,49, Energy bar EUR 2,49, Eiwit shake EUR 29,95, Boek, Personal
 *     training, Abonnement -- each one a buy button
 *     (src/pages/account/payments.vue). No history, no status, no subscription
 *     detail. Nothing sends this key today; it is kept because a later
 *     buy-something notification is a plausible use for it. The menu entry it
 *     backs is called "Betaling", and the owner decided on 2026-08-20 that it
 *     keeps that name.
 *
 *   `betalingen` -> /account/abonnement, the member's own subscriptions and
 *     payments: every subscription they have ever had, newest first, the newest
 *     one's Betalingen accordion already open. This is the target of
 *     PaymentFailedNotification (NOTIF-02), InvoiceAvailableNotification
 *     (NOTIF-03) and PaymentUpcomingNotification.
 *
 * The two names are the wrong way round and that is not a mistake anyone can
 * fix now: `abonnement` is the SERVER's key for the till and has been since
 * before phase 40, and `betalingen` is the server's key for the payment
 * notifications. The API is unchanged by 40-09 -- only where the client sends
 * `betalingen` moved. Pinned in both directions by
 * `betalingen is the subscription page and abonnement is the till -- they are
 * not swapped` (tests/js/push-taxonomy.test.js).
 *
 * The previous version of this comment justified `abonnement` as "the screen a
 * member reaches for their subscription IS the payments page". That is false
 * and it is corrected rather than left standing: it is a till, not a statement.
 * Landing "je betaling is mislukt" on a buy-drinks page is not tapping through
 * to the detail. GATE-40-B decided that and GATE-40-E keeps it.
 *
 * ============================================================================
 *  EVERY VALUE HERE IS A COMPLETE PATH. IT MUST STAY THAT WAY.
 * ============================================================================
 *
 * 40-05 made `betalingen` `/users/:user_id` and added `resolveSelfPath()` to
 * fill the parameter in from the SESSION at consume time -- never from the
 * payload, because the WebView being steered holds a bearer token that opens
 * the gym's door, and a payload that could name a member could deep-link one
 * member into another's account record.
 *
 * 40-09 retired both. The destination is now fixed, so a payload's id has
 * nowhere to GO: the whole class of "a push names a member" is CLOSED rather
 * than guarded, which is a stronger property than the helper provided.
 *
 * IF A FUTURE PLAN NEEDS A SELF-REFERENTIAL DESTINATION -- phase 44 adds more
 * notification types -- the rule that helper encoded still applies and is not
 * optional: the id comes from `rootState.current_user.data.id` at CONSUME time
 * and never from the payload. Consume time, not tap time, so a tap left pending
 * across a logout on a shared handset cannot deep-link the new holder into the
 * old member's record (T-40-22). Reintroducing a parameter reds
 * `no deep-link destination carries a route parameter any more`, which is where
 * that reader should arrive. The reasoning is written up in GATE-40-E.
 *
 * Every value is also the LITERAL declared route path from src/js/routes.js, so
 * tests/js/push-taxonomy.test.js's route-table parity assertion validates the
 * map with no edit, no special case and no exemption. That guard is the reason
 * the previous deep-link map's two broken paths were ever found.
 */
export const SCREEN_ROUTES = {
  home: '/',
  reserveringen: '/account/reserveringen',
  abonnement: '/account/payments',
  betalingen: '/account/abonnement',

  // THE NEWS LIST, NOT AN INDIVIDUAL ITEM -- and the reason is that phase 40
  // already spent the change that would have allowed the item (D-44-08).
  //
  // This value read '/' until 44-11. The app root has shown NO news since phase
  // 43 moved the cards onto the reservations page
  // (src/pages/account/reserveringen.vue:36-60), so a tapped news notification
  // landed on a screen with nothing on it about the thing that was tapped.
  // NEWS-08 says the notification opens the page where news IS shown; this is
  // that page, and tests/js/push-taxonomy.test.js's news assertion moved with it.
  //
  // D-44-08 said "the news list, UNLESS phase 40's resolveScreenRoute() change
  // has landed, in which case the item". 40-09 landed it in the OPPOSITE
  // direction -- it retired route parameters from this map entirely (see the
  // docblock above) -- so the item is not reachable and reintroducing
  // '/news/:news_id' would red `no deep-link destination carries a route
  // parameter any more`. The list is the destination, deliberately.
  nieuws: '/account/reserveringen',
};

export function resolveScreenRoute(screen) {
  return SCREEN_ROUTES[screen] || SCREEN_ROUTES.home;
}

/**
 * Push only exists on a real device. On web this is a no-op rather than an
 * error, so the same code path runs in the browser during development.
 */
export function isPushSupported() {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('PushNotifications');
}

export function currentPlatform() {
  return Capacitor.getPlatform();
}

/**
 * The two values the API accepts for `apns_environment`, and the only two Apple
 * has. Anything else is treated as if the build had said nothing.
 */
const APNS_ENVIRONMENTS = ['sandbox', 'production'];

/**
 * Which APNs environment this build's token belongs to (finding C3, client half).
 *
 * Sending to the wrong gateway is not an error -- Apple accepts the request and
 * silently drops it -- so the server stores this per token and filters on it.
 * A debug build talks to sandbox; TestFlight and App Store builds talk to
 * production.
 *
 * ============================================================================
 *  WHAT CHANGED, AND WHY THE OLD VERSION WAS WRONG
 * ============================================================================
 *
 * This used to be `import.meta.env.DEV ? 'sandbox' : 'production'` and nothing
 * else. That is a property of WHICH VITE MODE RAN, not of which entitlement the
 * app was signed with, and the two come apart in the most ordinary case there
 * is: a developer runs `npm run build` (mode production, so `DEV` is false),
 * copies the bundle into the native project and launches it from Xcode. The
 * handset holds a SANDBOX token; the app declared `production`. This repo's own
 * `make ios-simulator` target is exactly that sequence.
 *
 * The consequence was not a lost notification. The server sent through the
 * production gateway, Apple answered `BadDeviceToken`, and the reaper read that
 * as permanent and DELETED a live registry row -- so the handset lost push
 * entirely, and silently, until the member happened to log out and back in.
 *
 * PG_APNS_ENVIRONMENT is therefore read FIRST. It follows the project's existing
 * baked-at-build-time convention (`PG_API_ROOT_URL`, `PG_PUSHER_APP_KEY`,
 * `PG_APP_VERSION`) and reaches the bundle through the same `envPrefix: 'PG_'`
 * already configured in vite.config.mjs -- no new mechanism, nothing extra to
 * remember. Set it to `sandbox` for anything that will be run from Xcode, and to
 * `production` for TestFlight and the App Store. TestFlight is a PRODUCTION APNs
 * client despite being a test channel; that is the one people get wrong.
 *
 * THE FALLBACK IS DELIBERATE. Every build that ships today sets no such
 * variable, and each must keep declaring exactly what it declared before this
 * change -- so an absent value falls through to the old inference rather than to
 * an error or to a hard-coded guess. An UNRECOGNISED value falls through too,
 * because the server validates this field: a typo in a build script would
 * otherwise be rejected at registration and cost the member their notifications
 * outright.
 *
 * WHAT IS STILL NOT DONE, recorded rather than implied. The value remains
 * CLIENT-DECLARED. The unguessable version reads the signed `aps-environment`
 * entitlement natively, which needs Capacitor bridging or a small custom plugin
 * and whose size was never established (RESEARCH assumption A5, confidence LOW),
 * so a build that is configured wrongly -- or a tampered one -- can still lie.
 * What makes that survivable is the server half, shipped in plan 39.5-04:
 * `BadDeviceToken` is now quarantined instead of reaped, so a wrong declaration
 * costs a wasted send rather than a member's registration. The two halves are a
 * pair; neither is sufficient alone.
 *
 * @returns {'sandbox'|'production'|null} null on every platform but iOS, whatever
 *   the build says -- APNs environments are an Apple concept and the server
 *   stores null for android rows.
 */
export function apnsEnvironment() {
  if (currentPlatform() !== 'ios') {
    return null;
  }

  const declared = import.meta.env.PG_APNS_ENVIRONMENT;

  if (APNS_ENVIRONMENTS.includes(declared)) {
    return declared;
  }

  return import.meta.env.DEV ? 'sandbox' : 'production';
}

/**
 * Ask the OS for notification permission.
 *
 * Call this in context -- Phase 39 calls it after the member's first booking.
 * Never call it at app launch.
 *
 * @returns {Promise<'granted'|'denied'|'prompt'>}
 */
export async function requestPermission() {
  if (!isPushSupported()) {
    return 'denied';
  }

  const existing = await PushNotifications.checkPermissions();

  if (existing.receive === 'granted') {
    return 'granted';
  }

  // 'denied' here means the member already said no at OS level. Asking again
  // does nothing on iOS -- the prompt is one-shot -- so report it and let the
  // caller show a "turn it on in Settings" banner instead of a dead prompt.
  if (existing.receive === 'denied') {
    return 'denied';
  }

  const requested = await PushNotifications.requestPermissions();

  return requested.receive === 'granted' ? 'granted' : 'denied';
}

export async function checkPermission() {
  if (!isPushSupported()) {
    return 'denied';
  }

  const status = await PushNotifications.checkPermissions();

  return status.receive;
}

/**
 * Wire up the plugin's listeners and start registration.
 *
 * Registration is asynchronous and its result arrives on the 'registration'
 * event, not as a return value, so listeners must be attached before register()
 * is called or the token is missed.
 *
 * @param {object} handlers
 * @param {(token: string) => any}  handlers.onToken       called with the platform token
 * @param {(error: any) => any}     handlers.onError       registration failed
 * @param {(data: object) => any}   handlers.onTapped      member tapped a notification
 * @param {(data: object) => any}  [handlers.onReceived]   notification arrived in foreground
 */
export async function startRegistration(handlers = {}) {
  if (!isPushSupported()) {
    return false;
  }

  const { onToken, onError, onTapped, onReceived } = handlers;

  await PushNotifications.removeAllListeners();

  await PushNotifications.addListener('registration', (token) => {
    if (onToken) onToken(token.value);
  });

  await PushNotifications.addListener('registrationError', (error) => {
    if (onError) onError(error);
  });

  // Fires when the app is in the foreground. The OS does not display a banner
  // in that case, so the app decides what to show.
  await PushNotifications.addListener('pushNotificationReceived', (notification) => {
    if (onReceived) onReceived(notification);
  });

  // PUSH-07. Fires both when the app was already running and when it was
  // launched from a cold start by the tap -- Capacitor queues the event until a
  // listener exists, which is why listeners are attached before register().
  await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    if (onTapped) onTapped(action?.notification?.data || {});
  });

  await PushNotifications.register();

  return true;
}

/**
 * Tear this device's push session down: stop listening, and REVOKE THE OS
 * REGISTRATION (PUSH-04, finding C1).
 *
 * ============================================================================
 *  WHY THE SECOND HALF EXISTS, AND WHY REMOVING LISTENERS IS NOT ENOUGH
 * ============================================================================
 *
 * `removeAllListeners()` tears down this app's ability to HEAR from the OS. It
 * says nothing to Apple or Google, and neither of them stops delivering: the
 * device stays registered, the server keeps a live route to it, and every
 * notification still lands on the lock screen. Clearing localStorage does not
 * reach an APNs or FCM server either -- `unregister()` is the ONLY call in this
 * codebase that does ("This will delete a firebase token on Android, and
 * unregister APNS on iOS", @capacitor/push-notifications 7.0.7
 * dist/esm/definitions.d.ts:40-44).
 *
 * The harm this closes is not hypothetical and it is not about the app being
 * untidy. Member A logs out on the train with no connectivity and hands the
 * phone to member B. Without the call below, A's booking reminders and payment
 * failures arrive on B's lock screen indefinitely. The server's reaper cannot
 * help, because delivery SUCCEEDS -- there is no failure for it to react to.
 *
 * ORDER IS DELIBERATE: listeners first, then the plugin. `unregister()` can
 * itself produce plugin events, and routing those into handlers that are in the
 * middle of being torn down is at best noise and at worst a `registration`
 * callback writing a token back into the store during the very action that is
 * clearing it. There is a test asserting the recorded call order.
 *
 * THE REJECTION IS SWALLOWED, and that is a considered trade rather than
 * laziness. This runs inside logout; a plugin-level failure must not leave the
 * member unable to log out, and there is nothing the app can do about it anyway
 * -- the OS registration is not ours to edit by other means. It is survivable
 * because the two halves of the revocation are independent: the caller still
 * issues DELETE /push-tokens, and a row the server no longer holds cannot be
 * sent to. The dangerous case is BOTH halves failing, which is what the
 * pending-unregistration tombstone in src/store/modules/push.js exists for.
 *
 * @returns {Promise<boolean>} false on web or an unavailable plugin -- i.e.
 *   "there was no native registration here to revoke". Deliberately NOT used to
 *   report a plugin rejection: `false` already means one thing, and no caller
 *   could act on a second.
 */
export async function stopRegistration() {
  if (!isPushSupported()) {
    return false;
  }

  await PushNotifications.removeAllListeners();

  // The outcome is deliberately DISCARDED here -- see the docblock. Logout must not
  // fail because the OS refused, and there is nothing this app can do about it in
  // that moment anyway. The caller that DOES need to know is
  // `push/retry_pending_unregistration`, which is retrying a revocation that already
  // failed once and must not throw away a second failure; it calls
  // revokeOsRegistration() directly for that reason.
  await revokeOsRegistration();

  return true;
}

/**
 * Revoke this device's OS-level push registration, AND SAY WHETHER IT WORKED.
 *
 * The same plugin call `stopRegistration()` makes, with the one difference that
 * matters to finding F2: the outcome is reported instead of swallowed.
 *
 * WHY THAT DIFFERENCE EXISTS. `stopRegistration()` runs on the logout path, where a
 * failure is survivable and unreportable -- the member is logging out and cannot act
 * on "Apple declined". `push/retry_pending_unregistration` runs on the LOGIN path of
 * a handset that is carrying somebody else's outstanding revocation, and there the
 * outcome decides whether the tombstone can be discharged. A revocation that failed
 * twice and was recorded as done would leave the previous member's notifications
 * arriving on this handset with nothing left to try again, which is finding F2's
 * harm restated.
 *
 * THIS IS THE HALF OF THE REVOCATION THAT NEEDS NO SERVER AUTHORITY. DELETE
 * /push-tokens is scoped to the authenticated member, so a handset held by somebody
 * else cannot remove the previous owner's row. `unregister()` talks to Apple and
 * Google about the DEVICE, so whoever is holding the device can make it -- and once
 * it succeeds the old token is undeliverable, the server's next send to it fails and
 * the reaper removes the row.
 *
 * @returns {Promise<boolean>} true only when the plugin call actually resolved.
 *   False on web or an unavailable plugin (there was no native registration to
 *   revoke) and false when the plugin rejected. Those two are collapsed on purpose:
 *   the only caller acts on "is the OS route definitely gone", and neither case
 *   answers yes.
 */
export async function revokeOsRegistration() {
  if (!isPushSupported()) {
    return false;
  }

  try {
    await PushNotifications.unregister();
  } catch (e) {
    // Deliberately not logged, same as everywhere else on this path: a device
    // registry refusing is not something a member can be told about usefully. The
    // caller records it by keeping the tombstone.
    return false;
  }

  return true;
}

/**
 * Is this taxonomy entry safe to turn into a permanent Android channel?
 *
 * Every field below is REQUIRED before a channel may be created, and the reason is the
 * one-way nature of the operation. `createNotificationChannel()` applies a new importance
 * only when it is LOWER than the existing one -- it can never be raised. So a channel
 * created once from incomplete data is wrong on that device forever, and the only escape is
 * a new channel id, which every existing member then sees as a permanent duplicate entry in
 * their system settings.
 *
 * The specific trap this guards is an omitted `android_importance`. Android's documented
 * default for a channel created without one is IMPORTANCE_DEFAULT (3) -- so a response
 * missing the field would silently create `booking` at 3 instead of 4 and `news` at 3
 * instead of 2, uncorrectably. That is not hypothetical: the field was added to
 * GET /api/v1/notification-preferences in this same phase, so any device talking to an API
 * instance that predates it, or to a half-finished deploy, receives exactly that response.
 *
 * Skipping is strictly better than guessing. A category skipped today has NO channel, which
 * costs one launch's worth of correct importance for that category and heals itself on the
 * next registration once the field arrives. A category guessed today is wrong permanently.
 *
 * @param {object} category one entry from the server's taxonomy
 * @returns {boolean}
 */
function isCreatableChannel(category) {
  if (!category || typeof category !== 'object') {
    return false;
  }

  const hasId          = typeof category.category === 'string' && category.category.length > 0;
  const hasName        = typeof category.label === 'string' && category.label.length > 0;
  const hasImportance  = Number.isInteger(category.android_importance)
    && category.android_importance >= 0
    && category.android_importance <= 5;

  return hasId && hasName && hasImportance;
}

/**
 * Create the Android notification channels from the server's taxonomy (NOTIF-05, CONTEXT).
 *
 * Idempotent by design: recreating a channel with identical values is a no-op on Android,
 * and importance can only ever be LOWERED afterwards -- never raised. So this runs on every
 * registration rather than behind a one-shot flag, which makes it self-healing if a channel
 * was deleted or a previous attempt failed. A flag would turn a recoverable transient
 * failure into a permanent one.
 *
 * WHY UP FRONT, AND NOT LAZILY. A channel created on first use does not exist at the moment
 * the notification carrying its `channel_id` lands, and Android does not queue the message
 * waiting for one -- it falls back to FCM's auto-created "Miscellaneous" channel at DEFAULT
 * importance. So all five are created immediately after permission is granted, before
 * anything carrying a `channel_id` can arrive.
 *
 * `categories` comes from GET /api/v1/notification-preferences. The client holds no copy of
 * the Dutch labels and derives no importance itself; a second copy is what produced the
 * missing `marketing` case this phase had to fix. The `importance` and the channel `id` are
 * the server's verbatim -- deriving importance on the client, from the consent grounds or
 * from the category name or from anything else, would be a second source of truth for a
 * value that becomes IMMUTABLE on the device the moment the channel is created. The two
 * copies would then disagree silently and permanently.
 *
 * NOT SETTABLE HERE, DELIBERATELY: Do Not Disturb bypass. @capacitor/push-notifications
 * 7.0.7's Channel type has no `bypassDnd` field (definitions.d.ts:282-354, read, not
 * assumed), and Android's real DND bypass is a per-channel permission only the member can
 * grant from system settings. IMPORTANCE_HIGH (4) is the ceiling an app may set for itself,
 * so NOTIF-05's "break through Do Not Disturb" is only PARTIALLY achievable on Android. The
 * iOS half is a real time-sensitive interruption level; the Android half is not. Recorded
 * here rather than hidden, per GATE-39-B.
 *
 * @param {Array<{category: string, label: string, description: string, android_importance: number}>} categories
 * @returns {Promise<boolean>} false on a non-Android platform, when nothing was created, or
 *   when any single channel could not be created. Never throws: this sits in the middle of
 *   the registration sequence and a rejection here would cost the device its token.
 */
export async function createNotificationChannels(categories) {
  // Channels are an Android concept; iOS has categories and interruption levels instead.
  // isPushSupported() is checked too, for the same reason every other exported function in
  // this file checks it -- a native build where the plugin failed to load reports its
  // platform correctly but has nothing to call.
  if (!isPushSupported() || Capacitor.getPlatform() !== 'android') {
    return false;
  }

  // No taxonomy in hand means no channels. Never invent a fallback name: `name` is required
  // by the plugin, it is what the member reads in system settings, and a placeholder written
  // there today is what they still read months later.
  if (!Array.isArray(categories) || categories.length === 0) {
    return false;
  }

  let created = 0;
  let failed  = 0;

  for (const category of categories) {
    if (!isCreatableChannel(category)) {
      failed += 1;
      continue;
    }

    try {
      await PushNotifications.createChannel({
        id:          category.category,
        name:        category.label,
        description: category.description,
        importance:  category.android_importance,

        // VISIBILITY_PRIVATE. A security control, not a style choice: notification content
        // is redacted on a locked screen, so a booking confirmation or a payment failure is
        // not readable by whoever picks the phone up (PUSH-08). The server-side
        // PushPayloadGuard is the other half of the same control.
        visibility: 0,

        // Anything the member is meant to notice makes the phone move; the two silent
        // categories stay silent in every respect. Derived from the server's importance so
        // the two cannot be set inconsistently.
        vibration: category.android_importance >= 3,
      });

      created += 1;
    } catch (e) {
      // One channel failing must not abandon the remaining four -- they are independent, and
      // losing `booking` because `marketing` failed would be the worst possible trade. The
      // error is deliberately not logged: the taxonomy carries member-facing text and the
      // failure is retried on the next registration anyway.
      failed += 1;
    }
  }

  return failed === 0 && created > 0;
}

/**
 * Open THIS app's notification settings page in the OS settings app
 * (PREF-04 / IC3).
 *
 * Deliberately not @capacitor/app-launcher, which is already installed: its
 * Android openUrl can only emit ACTION_VIEW+URI, a package launch intent, or a
 * bare Intent(action) with NO extras -- and Android's app-notification settings
 * screen requires EXTRA_APP_PACKAGE. That combination is not expressible in JS,
 * which is the whole reason capacitor-native-settings is a dependency.
 *
 * IOSSettings.AppNotification, not IOSSettings.App: the plugin's Swift resolves
 * it to UIApplication.openNotificationSettingsURLString on iOS 16+ and falls
 * back to openSettingsURLString below that, so it is one tap closer and degrades
 * safely. (The plugin's own TS docblock says "iOS 15.4+"; its Swift says
 * `#available(iOS 16.0, *)`. The Swift is what runs.) Both are Apple SDK
 * constants -- the plugin's README warning about undocumented `App-prefs:`
 * schemes applies to its other ~30 enum values, which this app never references.
 *
 * The returned promise is deliberately NOT used to sequence UI. On Android
 * NativeSettings.open resolves via startActivityForResult -- i.e. when the
 * member CLOSES the settings activity, possibly minutes later. Callers must
 * treat the return value as "did the call succeed", never as "the member is
 * back". Re-checking permission on resume is a separate concern
 * (@capacitor/app appStateChange), not something to await here.
 *
 * @returns {Promise<boolean>} false on web, on an unavailable plugin, or on
 *   rejection -- so the caller shows the fallback toast rather than leaving the
 *   member stranded. This never throws: the web implementation of the plugin
 *   rejects with Error('Not implemented for web.'), and an unhandled rejection
 *   out of a button handler is a worse outcome than a false.
 */
export async function openOsNotificationSettings() {
  // isPushSupported() already covers both the web platform and a native build
  // where the plugin failed to load. Guarding on it rather than on
  // Capacitor.isNativePlatform() alone keeps this consistent with every other
  // exported function in this file, and means the plugin is never called in a
  // context where it can only reject.
  if (!isPushSupported()) {
    return false;
  }

  try {
    await NativeSettings.open({
      optionAndroid: AndroidSettings.AppNotification,
      optionIOS: IOSSettings.AppNotification,
    });

    return true;
  } catch (e) {
    return false;
  }
}
